// §4.6 — THE REVIEW INBOX, /sales/reviews (Module 09, slice P3-C-1).
//
// The Big Boss's "what is wrong at a glance" screen, and the D-231 seam: in P3
// the pending-review decision is made ON THE DOCUMENT, so this module owns the
// inbox and module 12 absorbs it in P4 as its first consumer. The state names,
// the endpoints, the permissions and the events are all chosen so that
// absorption is additive and never a rename.
//
// IT IS A CARD LIST, NOT A `DataTable`, and that is a design decision rather
// than a shortcut. The four kinds carry DIFFERENT VERBS — a margin breach is
// Approve/Reject, a credit block is Release/Record-prepayment/Uphold, a
// fulfilment degradation has no verb at all — and different evidence: breach
// lines with margin-vs-guard for one, an over-limit amount for another. A
// table would need a column that is empty for three kinds out of four and an
// actions cell that means something different per row. Cards let each kind
// state its own case in its own shape, which is what "one row per item,
// amount, requester, age" actually asks for once the kinds differ.
//
// WHAT CAN BE DECIDED HERE, AND WHAT HONESTLY CANNOT. §4.6 describes five card
// kinds; the decide path is mounted for FOUR of them:
//
//   quote_margin_breach     → POST /v1/quotes/{id}/decide      ✓ P3-C-1
//   credit_block            → POST /v1/orders/{id}/credit-decision ✓ P3-C-1
//   price_override          → POST /v1/customer-price-overrides/{id}/decide ✓ P3-C-2 (D-263)
//   order_margin_breach     → POST /v1/orders/{id}/decide       ✓ P3-D-1 (D-283)
//   fulfillment_degradation → nothing, BY DESIGN (§4.6: informational)
//
// Until P3-D-1 the order kind's route was absent, and its card rendered with
// its evidence and a sentence naming the slice that would bring the decision
// — never a button into a 404. `pendingDecisionKey` is that mechanism, kept
// with no kind left in it: a hidden card is a review nobody knows is
// outstanding, so the next kind whose route lands after its card will render
// the same way rather than be hidden.
//
// THE ORDER CARD'S VERBS ARE THE QUOTE CARD'S. Approve / Reject under
// `sales.margin.approve`, the same mandatory note on reject, the same
// server-side threshold (`decidable_by_caller`, re-derived by the server from
// the breach facts STORED on the order line — §9.3, never by repricing), and
// the same group-admin threshold copy: a direct order tiers on depth OR line
// size exactly as a quote does (§3.4). "Open" lands on the order itself,
// where each breaching line carries its needs-approval / off-list chip.
//
// A DECIDE THAT ANSWERS 409 `ORDER_NOT_PENDING` OR `ORDER_TERMINAL` IS NOT
// THE VERB FAILING. It is the server saying the order is no longer waiting —
// another desk decided it first, or an admin withdrew it (§3.6). Rendering
// that as a failure note on a card still offering Approve would invite a
// second press into the same 409. The card LEAVES the desk, as a decided one
// does, and a notice above the feed carries the document number and the
// server's own reason, so the reader knows what happened and why the card is
// gone. Any other refusal (a 403 over the threshold, a network cut) keeps the
// card and its failure note — the card is still waiting.
//
// THE OVERRIDE CARD'S DEEP LINK IS THE CUSTOMER'S PRICING TAB. An override has
// no document screen of its own — §2.9 is a row on a customer, and §4.2's
// Pricing tab is the surface that lists it with its validity and state — so
// "Open" lands the reviewer there (`?tab=pricing`, search-params.ts). The
// Approve / Reject pair rides `sales.margin.approve` exactly as the quote
// breach does (D-231), with the same mandatory-note-on-reject and the same
// server-side threshold (`decidable_by_caller`). A decided card LEAVES the
// feed at once: the screen drops the decided key itself rather than waiting
// for the page to be read again. The feed shows one cursor page at a time
// (D-560), with Previous and Next under it.
//
// THE THRESHOLD IS THE SERVER'S. §3.4 gates a decision on the breach size
// (≤2pp facility_admin; above it, or a large breached line, group_admin only)
// and only the server can evaluate that. `decidable_by_caller` is the wire's
// answer for THIS reader and THIS card; the screen offers buttons on it and
// states the threshold copy when it is false. It never re-derives the rule —
// a client that guessed would either offer a 403 or hide a grant a group_admin
// genuinely holds.
//
// §7 REDACTION IS ABSENCE, again: `amount_minor` is omitted for a reader
// without `sales.read_value`, and the amount line then does not render. The
// card is still worth reading without it — which is the test of whether an
// omission was designed or merely tolerated.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { SalesFilterBar } from "../components/sales-filter-bar";
import { SalesGroupTabs } from "../components/group-tabs";
import { Button } from "../../../../components/button";
import { ListPager } from "../../../../components/list-pager";
import { Card } from "../../../../components/card";
import { Field } from "../../../../components/field";
import { RefLabel } from "../../../../components/ref-label";
import { Select } from "../../../../components/select";
import {
  CardSkeleton,
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { formatMinor } from "../lib/money";
import { ageInDays, reviewIsDecidable, reviewKindVariant, type ReviewKind } from "../lib/quotes";
import { MutationFailureNote } from "../mutation-failure-note";
import { isSalesReadRefused, useSalesPermissions } from "../hooks/permissions";
import { useOwnerIndex } from "../hooks/sales-queries";
import { useMemberRefAsk } from "../lib/reads";
import { useDecideOrder } from "../hooks/order-mutations";
import {
  useCreditDecision,
  useDecideOverride,
  useDecideQuote,
  useReviews,
  type ReviewCard as ReviewCardRow,
  type ReviewsQuery,
} from "../hooks/quote-queries";
import { bookRange, useBookPages, useBookWindow } from "../hooks/book-pages";

const KINDS = [
  "quote_margin_breach",
  "order_margin_breach",
  "price_override",
  "credit_block",
  "fulfillment_degradation",
] as const;

/**
 * A review card's identity.
 *
 * `ReviewCard` deliberately has no `id`: it is a PROJECTION over four
 * different tables, and minting a synthetic row id would invent an entity the
 * server does not have. Its identity is therefore the pair (kind, subject) —
 * exactly one of `quote_id`/`order_id`/`override_id` is non-null and it
 * matches `kind` — which is unique because one document can only be waiting on
 * one review of one sort at a time. The feed's React keys and the set of
 * cards decided here are both keyed on it.
 */
function cardKey(card: ReviewCardRow): string {
  return `${card.kind}:${card.quote_id ?? card.order_id ?? card.override_id ?? card.document_no}`;
}

/**
 * Which slice brings the decision for a kind whose route is not mounted yet —
 * `undefined` for the kinds that CAN be decided here, and for the one that is
 * informational by design.
 *
 * Empty since P3-D-1 (D-283): the order kind's decide was the last route to
 * land after its card. Kept, with its `sales-review-pending` note, so that a
 * future kind arriving in the same order renders as this one did — visible,
 * with its evidence, and honest about the absent verb.
 */
function pendingDecisionKey(_kind: ReviewKind): string | undefined {
  return undefined;
}

/**
 * The 409 codes that mean "this order is no longer waiting for a decision":
 * decided by another desk (`ORDER_NOT_PENDING`) or withdrawn by an admin
 * (`ORDER_TERMINAL`, §3.6). Either way the card has nothing left to decide.
 */
const ORDER_DEPARTED_CODES: ReadonlySet<string> = new Set(["ORDER_NOT_PENDING", "ORDER_TERMINAL"]);

/** A card that left the desk without a decision from THIS reader — the
 * document number and the server's own reason, rendered above the feed. */
interface Departure {
  key: string;
  documentNo: string;
  serverTitle: string | undefined;
}

export function SalesReviewsScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  const owners = useOwnerIndex(perms.isAdmin);

  const [kind, setKind] = useState("");
  // Keys decided in THIS mount — dropped from the feed at once (see header).
  const [decided, setDecided] = useState<ReadonlySet<string>>(() => new Set());
  // Cards that left WITHOUT this reader's decision (a 409 saying the order is
  // no longer pending) — dropped from the feed like a decided one, and named
  // above it with the server's reason, so the reader knows why it is gone.
  const [departed, setDeparted] = useState<readonly Departure[]>([]);

  const query: ReviewsQuery = useMemo(
    () => (kind.length > 0 ? { kind: kind as NonNullable<ReviewsQuery["kind"]> } : {}),
    [kind],
  );
  const queryKey = JSON.stringify(query);
  useEffect(() => {
    setDecided(new Set());
    setDeparted([]);
  }, [queryKey]);

  const refused = isSalesReadRefused(perms, perms.canOpenReviews);
  // One page at a time (D-560): the stack starts again at page 1 whenever
  // the kind does.
  const pages = useBookPages(queryKey);
  const page = useReviews(query, pages.cursor, !refused);
  const served = useMemo(() => page.data?.items ?? [], [page.data]);
  // The page's cards as the reader left them: a card decided here leaves at
  // once. A warm remount (deciding a card navigates away and back) paints the
  // cached page on its first pass.
  const rows = useMemo(
    () => served.filter((card) => !decided.has(cardKey(card))),
    [served, decided],
  );
  // The range counts the page the server served; a page whose every card was
  // decided here steps back once no read of it is in flight.
  const pageView = useBookWindow(pages, {
    items: served,
    nextCursor: page.data?.next_cursor,
    turning: page.isPlaceholderData,
    settled: page.data !== undefined && !page.isFetching,
    visible: rows.length,
  });

  useEffect(() => {
    if (page.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [page.error, navigate]);

  if (refused) {
    return <ForbiddenState title={t("sales.access.title")} body={t("sales.access.reviews")} />;
  }

  return (
    <section className="sales-reviews-screen" data-testid="sales-reviews-screen">
      <PageHeader title={t("sales.reviews.title")} lede={t("sales.reviews.intro")} />
      <SalesGroupTabs group="tasks" />

      <SalesFilterBar
        data-testid="sales-reviews-filters"
        filters={[
          {
            key: "kind",
            inline: true,
            // On a phone the select moves into the sheet, so the chip is
            // what says the queue shows one kind of review.
            applied:
              kind === ""
                ? undefined
                : t("sales.common.filterChip", {
                    filter: t("sales.reviews.filterKind"),
                    value: t(`sales.reviews.kind.${kind}`),
                  }),
            onClear: () => {
              setKind("");
            },
            control: (
              <Field label={t("sales.reviews.filterKind")}>
                <Select
                  value={kind}
                  data-testid="reviews-kind"
                  onChange={(e) => {
                    setKind(e.target.value);
                  }}
                >
                  <option value="">{t("sales.quotes.filterAll")}</option>
                  {KINDS.map((value) => (
                    <option key={value} value={value}>
                      {t(`sales.reviews.kind.${value}`)}
                    </option>
                  ))}
                </Select>
              </Field>
            ),
          },
        ]}
      />

      {page.fetchStatus === "paused" && rows.length === 0 ? (
        <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />
      ) : null}

      {page.isPending && page.fetchStatus !== "paused" && rows.length === 0 ? (
        <CardSkeleton lines={6} label={t("sales.common.loading")} />
      ) : null}

      {page.error !== null && !(page.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("sales.reviews.error")}
          retryLabel={t("sales.common.retry")}
          requestIdLabel={t("sales.common.requestId")}
          requestId={requestIdOf(page.error)}
          onRetry={() => {
            void page.refetch();
          }}
        />
      ) : null}

      {!page.isPending &&
      page.fetchStatus !== "paused" &&
      page.error === null &&
      rows.length === 0 ? (
        <EmptyState title={t("sales.reviews.emptyTitle")} body={t("sales.reviews.emptyBody")} />
      ) : null}

      {departed.map((gone) => (
        <p
          key={gone.key}
          className="sales-hint"
          role="status"
          data-testid="sales-review-departed"
          data-document-no={gone.documentNo}
        >
          {t("sales.reviews.departed", { no: gone.documentNo })}
          {gone.serverTitle !== undefined && gone.serverTitle.length > 0 ? (
            <> {t("sales.common.serverSaid", { title: gone.serverTitle })}</>
          ) : null}
        </p>
      ))}

      <div className="sales-review-feed">
        {rows.map((card) => (
          <ReviewCardView
            key={cardKey(card)}
            card={card}
            requesterName={owners.byId.get(card.requested_by)}
            requesterPending={owners.isPending}
            locale={i18n.language}
            onDecided={() => {
              setDecided((prior) => new Set([...prior, cardKey(card)]));
            }}
            onDeparted={(serverTitle) => {
              const key = cardKey(card);
              setDecided((prior) => new Set([...prior, key]));
              setDeparted((prior) =>
                prior.some((gone) => gone.key === key)
                  ? prior
                  : [...prior, { key, documentNo: card.document_no, serverTitle }],
              );
            }}
          />
        ))}
      </div>

      <ListPager
        range={bookRange(t, i18n.language, pageView)}
        hasPrev={pageView.hasPrev}
        hasNext={pageView.hasNext}
        busy={page.isPlaceholderData}
        busyLabel={t("sales.common.loading")}
        prevLabel={t("sales.common.pagePrev")}
        nextLabel={t("sales.common.pageNext")}
        onPrev={pages.prev}
        onNext={() => {
          const next = page.data?.next_cursor;
          if (page.isPlaceholderData || next === null || next === undefined) return;
          pages.next(next, served.length);
        }}
        testId="sales-reviews-pager"
      />
    </section>
  );
}

function ReviewCardView({
  card,
  requesterName,
  requesterPending,
  locale,
  onDecided,
  onDeparted,
}: {
  card: ReviewCardRow;
  requesterName: string | undefined;
  requesterPending: boolean;
  locale: string;
  /** The decision landed (2xx): the feed drops this card. */
  onDecided: () => void;
  /** The server answered that the document is no longer waiting (409
   * `ORDER_NOT_PENDING` / `ORDER_TERMINAL`): the feed drops this card and
   * names the reason — the server's problem title when it sent one. */
  onDeparted: (serverTitle: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [note, setNote] = useState("");
  const [noteMissing, setNoteMissing] = useState(false);

  const decideQuote = useDecideQuote(card.quote_id ?? "");
  const decideCredit = useCreditDecision();
  const decideOverride = useDecideOverride(card.override_id ?? "");
  const decideOrder = useDecideOrder(card.order_id ?? "");
  const memberRef = useMemberRefAsk(requesterPending);

  /** The order decide's refusal handler: a "no longer pending" 409 is a
   * departure (see header), anything else stays on the card as a failure. */
  const onOrderDecideError = (error: unknown) => {
    if (
      error instanceof RequestError &&
      error.code !== undefined &&
      ORDER_DEPARTED_CODES.has(error.code)
    ) {
      onDeparted(error.problem?.title);
    }
  };

  const kind: ReviewKind = card.kind;
  const age = ageInDays(card.requested_at, Date.now());
  const pendingKey = pendingDecisionKey(kind);
  const canDecideHere = reviewIsDecidable(kind) && pendingKey === undefined;
  /** The threshold sentence this KIND's tier is described by, or `undefined`
   * for a kind whose decide rule is a grant and nothing else. */
  const thresholdKey =
    kind === "credit_block"
      ? undefined
      : kind === "price_override"
        ? "sales.reviews.thresholdOverride"
        : "sales.reviews.thresholdGroup";

  const openDocument = () => {
    if (card.quote_id !== null) {
      void navigate({ to: "/sales/quotes/$quoteId", params: { quoteId: card.quote_id } });
      return;
    }
    if (card.order_id !== null) {
      void navigate({ to: "/sales/orders/$orderId", params: { orderId: card.order_id } });
      return;
    }
    if (card.override_id !== null) {
      // The override lives on its customer (§2.9): the Pricing tab lists it.
      void navigate({
        to: "/sales/customers/$customerId",
        params: { customerId: card.customer_id },
        search: { tab: "pricing" },
      });
    }
  };

  return (
    <Card
      className="sales-review-card"
      data-testid="sales-review-card"
      data-kind={kind}
      title={card.document_no}
      action={
        <StatusChip variant={reviewKindVariant(kind)} data-testid="sales-review-kind">
          {t(`sales.reviews.kind.${kind}`)}
        </StatusChip>
      }
    >
      <p className="sales-360-idline">
        <span>{card.customer_display_name}</span>
        {/* §7 omission — no `sales.read_value`, no amount line at all. */}
        {card.amount_minor !== undefined ? (
          <span className="num" data-testid="sales-review-amount">
            {kind === "price_override" ? t("sales.reviews.amountUnit") : t("sales.reviews.amount")}:{" "}
            {formatMinor(card.amount_minor, card.currency, locale)}
          </span>
        ) : null}
        <span>
          {t("sales.reviews.requester")}:{" "}
          <RefLabel
            value={requesterName}
            id={card.requested_by}
            pending={requesterPending}
            {...memberRef}
          />
        </span>
        <span className="num" data-testid="sales-review-age">
          {age === undefined || age === 0
            ? t("sales.reviews.ageToday")
            : t("sales.reviews.age", { count: age })}
        </span>
      </p>

      {card.breach_lines.length > 0 ? (
        <ul className="sales-review-lines">
          {card.breach_lines.map((line) => (
            <li key={line.line_no} data-testid="sales-review-line">
              <span>
                {t("sales.reviews.line", { line: line.line_no })} · {line.description}
              </span>
              {/* The numbers are §7-omitted together; without them the line
                  still names itself and the off-list fact, which is what a
                  reader without the grant is entitled to. */}
              {line.margin_pct !== undefined && line.guard_pct !== undefined ? (
                <span className="num" data-testid="sales-review-breach">
                  {t("sales.reviews.lineBreach", {
                    margin: line.margin_pct,
                    guard: line.guard_pct,
                  })}
                </span>
              ) : null}
              {line.delta_minor !== undefined ? (
                <span className="num">
                  {t("sales.reviews.lineImpact", {
                    amount: formatMinor(line.delta_minor, card.currency, locale),
                  })}
                </span>
              ) : null}
              {line.off_list ? (
                <StatusChip variant="bad" data-testid="sales-review-off-list">
                  {t("sales.priceSource.offList")}
                </StatusChip>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {card.over_limit_minor !== undefined ? (
        <p className="num" data-testid="sales-review-over-limit">
          {t("sales.reviews.creditOver", {
            amount: formatMinor(card.over_limit_minor, card.currency, locale),
          })}
        </p>
      ) : null}
      {card.credit_check_status === "blocked_prepay" ? (
        <p className="sales-hint">{t("sales.reviews.creditPrepayOnly")}</p>
      ) : null}

      {kind === "fulfillment_degradation" ? (
        <p className="sales-hint" data-testid="sales-review-informational">
          {t("sales.reviews.informational")}
        </p>
      ) : null}

      {pendingKey !== undefined ? (
        <p className="sales-pending-note" data-testid="sales-review-pending">
          {t(pendingKey)}
        </p>
      ) : null}

      {canDecideHere && !card.decidable_by_caller ? (
        // The threshold copy per kind: a quote breach tiers on depth OR line
        // size (§3.4); an override tiers on depth ALONE (D-263 — a rate has
        // no line total), so its sentence must not claim a money bar.
        //
        // A CREDIT BLOCK HAS NO TIER AT ALL, and used to be told it did. The
        // server's rule is `sales.credit.approve`, full stop (reviews.service
        // — no threshold in the credit branch), so the margin sentence "the
        // breach is over 2 points, or a line is large" was describing a bar
        // that does not exist to a reader whose real problem is a grant they
        // do not hold. `cannotDecide` alone is the whole truth there.
        <p className="sales-hint" data-testid="sales-review-threshold">
          {t("sales.reviews.cannotDecide")}
          {thresholdKey !== undefined ? <> {t(thresholdKey)}</> : null}
        </p>
      ) : null}

      {noteMissing ? (
        <p className="sales-error-note" role="alert" data-testid="sales-review-note-required">
          {t("sales.reviews.noteRequired")}
        </p>
      ) : null}

      {decideQuote.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.reviews.decideFailed")}
          error={decideQuote.error}
        />
      ) : null}
      {decideCredit.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.reviews.decideFailed")}
          error={decideCredit.error}
        />
      ) : null}
      {decideOverride.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.reviews.decideFailed")}
          error={decideOverride.error}
        />
      ) : null}
      {decideOrder.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.reviews.decideFailed")}
          error={decideOrder.error}
        />
      ) : null}

      <div className="sales-toolbar">
        {/* ONE note input for every decidable kind. It used to be declared
            inside the two margin branches, so the three CREDIT verbs — which
            have always spread `note` into their payload — could only ever send
            an empty one: a release, a prepayment and an upheld stop were
            recorded with no reason a later reader could see. A decision a
            factory's finance lead has to justify is exactly the one that needs
            the field. */}
        {canDecideHere && card.decidable_by_caller ? (
          <Field label={t("sales.reviews.note")}>
            <input
              value={note}
              data-testid="sales-review-note"
              onChange={(e) => {
                setNote(e.target.value);
                setNoteMissing(false);
              }}
            />
          </Field>
        ) : null}

        {card.quote_id !== null || card.order_id !== null ? (
          <Button size="sm" data-testid="sales-review-open" onClick={openDocument}>
            {t("sales.reviews.open")}
          </Button>
        ) : card.override_id !== null ? (
          <Button size="sm" data-testid="sales-review-open" onClick={openDocument}>
            {t("sales.reviews.openCustomerPricing")}
          </Button>
        ) : null}

        {canDecideHere && card.decidable_by_caller && kind === "quote_margin_breach" ? (
          <>
            <Button
              size="sm"
              variant="primary"
              data-testid="sales-review-approve"
              busy={decideQuote.isPending}
              busyLabel={t("sales.reviews.deciding")}
              onClick={() => {
                setNoteMissing(false);
                decideQuote.mutate(
                  { outcome: "approved", ...(note.length > 0 ? { note } : {}) },
                  { onSuccess: onDecided },
                );
              }}
            >
              {t("sales.reviews.approve")}
            </Button>
            <Button
              size="sm"
              variant="danger"
              data-testid="sales-review-reject"
              busy={decideQuote.isPending}
              busyLabel={t("sales.reviews.deciding")}
              onClick={() => {
                // §5 makes the note MANDATORY on reject. Caught here so the
                // reader is told before the round trip — the server still
                // enforces it (`DECISION_NOTE_REQUIRED`), which is the point
                // of two layers.
                if (note.trim().length === 0) {
                  setNoteMissing(true);
                  return;
                }
                decideQuote.mutate({ outcome: "rejected", note }, { onSuccess: onDecided });
              }}
            >
              {t("sales.reviews.reject")}
            </Button>
          </>
        ) : null}

        {canDecideHere && card.decidable_by_caller && kind === "order_margin_breach" ? (
          <>
            <Button
              size="sm"
              variant="primary"
              data-testid="sales-review-approve"
              busy={decideOrder.isPending}
              busyLabel={t("sales.reviews.deciding")}
              onClick={() => {
                setNoteMissing(false);
                decideOrder.mutate(
                  { outcome: "approved", ...(note.length > 0 ? { note } : {}) },
                  { onSuccess: onDecided, onError: onOrderDecideError },
                );
              }}
            >
              {t("sales.reviews.approve")}
            </Button>
            <Button
              size="sm"
              variant="danger"
              data-testid="sales-review-reject"
              busy={decideOrder.isPending}
              busyLabel={t("sales.reviews.deciding")}
              onClick={() => {
                // §5 / §3.6: the note is MANDATORY on reject and becomes the
                // order's `cancelled_reason` verbatim. Caught before the round
                // trip; the service enforces it too (`DECISION_NOTE_REQUIRED`)
                // — the same two layers as the quote and override cards.
                if (note.trim().length === 0) {
                  setNoteMissing(true);
                  return;
                }
                decideOrder.mutate(
                  { outcome: "rejected", note },
                  { onSuccess: onDecided, onError: onOrderDecideError },
                );
              }}
            >
              {t("sales.reviews.reject")}
            </Button>
          </>
        ) : null}

        {canDecideHere && card.decidable_by_caller && kind === "price_override" ? (
          <>
            <Button
              size="sm"
              variant="primary"
              data-testid="sales-review-approve"
              busy={decideOverride.isPending}
              busyLabel={t("sales.reviews.deciding")}
              onClick={() => {
                setNoteMissing(false);
                decideOverride.mutate(
                  { outcome: "approved", ...(note.length > 0 ? { note } : {}) },
                  { onSuccess: onDecided },
                );
              }}
            >
              {t("sales.reviews.approve")}
            </Button>
            <Button
              size="sm"
              variant="danger"
              data-testid="sales-review-reject"
              busy={decideOverride.isPending}
              busyLabel={t("sales.reviews.deciding")}
              onClick={() => {
                // §5: the note is MANDATORY on reject and becomes the row's
                // reason. Caught before the round trip; the service enforces
                // it too (`DECISION_NOTE_REQUIRED`) — D-040's two layers.
                if (note.trim().length === 0) {
                  setNoteMissing(true);
                  return;
                }
                decideOverride.mutate({ outcome: "rejected", note }, { onSuccess: onDecided });
              }}
            >
              {t("sales.reviews.reject")}
            </Button>
          </>
        ) : null}

        {canDecideHere && card.decidable_by_caller && kind === "credit_block" ? (
          <>
            <Button
              size="sm"
              variant="primary"
              data-testid="sales-credit-release"
              busy={decideCredit.isPending}
              busyLabel={t("sales.reviews.deciding")}
              onClick={() => {
                if (card.order_id === null) return;
                decideCredit.mutate(
                  {
                    orderId: card.order_id,
                    body: { outcome: "approved", ...(note.length > 0 ? { note } : {}) },
                  },
                  { onSuccess: onDecided },
                );
              }}
            >
              {t("sales.reviews.creditApprove")}
            </Button>
            <Button
              size="sm"
              data-testid="sales-credit-prepay"
              busy={decideCredit.isPending}
              busyLabel={t("sales.reviews.deciding")}
              onClick={() => {
                if (card.order_id === null) return;
                decideCredit.mutate(
                  {
                    orderId: card.order_id,
                    body: {
                      outcome: "prepayment_recorded",
                      ...(note.length > 0 ? { note } : {}),
                    },
                  },
                  { onSuccess: onDecided },
                );
              }}
            >
              {t("sales.reviews.creditPrepay")}
            </Button>
            <Button
              size="sm"
              variant="danger"
              data-testid="sales-credit-uphold"
              busy={decideCredit.isPending}
              busyLabel={t("sales.reviews.deciding")}
              onClick={() => {
                if (card.order_id === null) return;
                decideCredit.mutate(
                  {
                    orderId: card.order_id,
                    body: { outcome: "upheld", ...(note.length > 0 ? { note } : {}) },
                  },
                  { onSuccess: onDecided },
                );
              }}
            >
              {t("sales.reviews.creditUphold")}
            </Button>
          </>
        ) : null}
      </div>
    </Card>
  );
}
